import { useNavigate } from '@tanstack/react-router'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { HOME_COPY } from '../features/home/config/copy'
import { HOME_CTA } from '../features/home/config/cta-routes'
import { useHomeStateQuery } from '../features/home/hooks/use-home-state'
import { useBasePath } from '../hooks/use-base-path'

/**
 * The founder's next action, on every page.
 *
 * Reads the same query and the same copy and route tables as the Home card, so
 * the button and the card can never point at different things — that was the
 * whole reason not to give the sidebar its own endpoint.
 *
 * Actions that need a dialog live on Home, so those states route there instead
 * of trying to open a dialog from the sidebar; the card then offers the real
 * control. Navigation targets go straight to the destination.
 */
export function HomeSidebarCta({ startupId }: { startupId: string }) {
  const navigate = useNavigate()
  const basePath = useBasePath()
  const homeQuery = useHomeStateQuery(startupId)

  const data = homeQuery.data?.data
  if (!data) return null

  const copy = HOME_COPY[data.key]
  // No ghost fallback: the sidebar carries the one next action, and a card's
  // secondary action is by definition not that. Falling through to it turned
  // "Message support" into the only button in the menu while the founder was
  // simply waiting for a link.
  // `??` would treat an explicit null as "unset" and fall back to the card's
  // CTA, which is the opposite of what null means here.
  const label = copy.menuCta === undefined ? copy.cta : copy.menuCta
  if (!label) return null

  const target = HOME_CTA[data.key].primary ?? HOME_CTA[data.key].ghost

  const go = () => {
    if (target?.kind === 'route') {
      navigate({ to: `${basePath}${target.to}`, search: target.search as never })
      return
    }
    // Dialogs and mutations are mounted by Home, so send the founder to the
    // card that owns them rather than half-performing the action here.
    navigate({ to: basePath })
  }

  return (
    <CtaButton onClick={go} arrow fullWidth className="text-center leading-tight">
      {label}
    </CtaButton>
  )
}
